import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { INCIDENT, fmtDM } from "@/lib/traffic";
import { fmtNum } from "@/lib/format";
import { cn } from "@/lib/utils";

export const KIND_COLORS = { wrong_way: "#E3120B", stopped: "#f59e0b", slow_down: "#1428A0", other: "#94a3b8" };
const KINDS = ["wrong_way", "stopped", "slow_down", "other"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border/70 bg-popover px-3 py-2 text-xs shadow-lg">
      <div className="mb-1 font-semibold">{label}</div>
      {payload.filter((p) => p.value).map((p) => <div key={p.dataKey} className="flex justify-between gap-4"><span style={{ color: p.color || p.fill }}>{p.name}</span><span className="num">{fmtNum(p.value)}</span></div>)}
    </div>
  );
}

// Incidents per day, stacked by type (day-first labels); every day of the range is shown, including days without incidents.
export function DayStack({ byDayKind, start, end }) {
  const days = [];
  if (start && end) {
    for (let d = new Date(`${start}T00:00:00`); d <= new Date(`${end}T00:00:00`) && days.length < 400; d.setDate(d.getDate() + 1)) {
      days.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
    }
  }
  const keys = days.length ? days : Object.keys(byDayKind || {});
  const rows = keys.map((d) => ({ label: fmtDM(d), ...(byDayKind?.[d] || {}) }));
  return (
    <div className="h-52" data-testid="incidents-day-stack">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ left: -20, right: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval={rows.length > 14 ? Math.ceil(rows.length / 14) - 1 : 0} />
          <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
          <Tooltip content={<Tip />} cursor={{ fill: "hsl(var(--secondary))" }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {KINDS.map((k, i) => <Bar key={k} dataKey={k} name={INCIDENT[k]?.label || k} stackId="k" fill={KIND_COLORS[k]} radius={i === KINDS.length - 1 ? [4, 4, 0, 0] : 0} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Share of incidents by type.
export function KindDonut({ byKind, total }) {
  const data = KINDS.filter((k) => byKind?.[k]).map((k) => ({ name: INCIDENT[k]?.label || k, value: byKind[k], k }));
  return (
    <div className="relative h-52" data-testid="incidents-kind-donut">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="82%" paddingAngle={2} stroke="none" isAnimationActive={false}>
            {data.map((d) => <Cell key={d.k} fill={KIND_COLORS[d.k]} />)}
          </Pie>
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12 }} formatter={(v, n) => [`${fmtNum(v)} (${total ? Math.round((100 * v) / total) : 0}%)`, n]} />
          <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center pb-6">
        <span className="num text-2xl font-semibold">{fmtNum(total || 0)}</span>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">incidents</span>
      </div>
    </div>
  );
}

// Weekday × hour-of-day heat map: when do incidents happen?
export function HourHeatmap({ matrix }) {
  const m = matrix || DOW.map(() => Array(24).fill(0));
  const max = Math.max(1, ...m.flat());
  return (
    <div data-testid="incidents-heatmap">
      <div className="grid gap-px" style={{ gridTemplateColumns: "36px repeat(24, minmax(0, 1fr))" }}>
        <div />
        {Array.from({ length: 24 }, (_, h) => <div key={h} className="num text-center text-[9px] text-muted-foreground">{h % 3 === 0 ? `${String(h).padStart(2, "0")}` : ""}</div>)}
        {m.map((row, d) => (
          <div key={d} className="contents">
            <div className="pr-1 text-right text-[10px] leading-5 text-muted-foreground">{DOW[d]}</div>
            {row.map((n, h) => (
              <div key={h} data-testid={`heat-${d}-${h}`} title={`${DOW[d]} ${String(h).padStart(2, "0")}:00 · ${n} incident${n === 1 ? "" : "s"}`}
                className={cn("h-5 rounded-[3px]", n === 0 && "bg-secondary/70")} style={n ? { backgroundColor: `rgba(227, 18, 11, ${0.18 + 0.82 * (n / max)})` } : undefined} />
            ))}
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>Hour of day (MYT) across the selected range</span>
        <span className="flex items-center gap-1">fewer <span className="inline-block h-2.5 w-10 rounded-sm" style={{ background: "linear-gradient(90deg, rgba(227,18,11,0.18), rgba(227,18,11,1))" }} /> more</span>
      </div>
    </div>
  );
}

// Hot spots: top sites as horizontal bars stacked by type.
export function SiteStack({ bySiteKind, names }) {
  const rows = Object.entries(bySiteKind || {}).map(([s, kinds]) => ({ site: s, name: names?.[s] || s, total: Object.values(kinds).reduce((a, b) => a + b, 0), ...kinds }))
    .sort((a, b) => b.total - a.total).slice(0, 8);
  if (!rows.length) return <p className="text-xs text-muted-foreground">No incidents in this range.</p>;
  return (
    <div className="h-52" data-testid="incidents-site-stack">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} layout="vertical" margin={{ left: 0, right: 24, top: 0, bottom: 0 }} barCategoryGap={4}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
          <Tooltip content={<Tip />} cursor={{ fill: "hsl(var(--secondary))" }} />
          {KINDS.map((k, i) => <Bar key={k} dataKey={k} name={INCIDENT[k]?.label || k} stackId="k" fill={KIND_COLORS[k]} radius={i === KINDS.length - 1 ? [0, 4, 4, 0] : 0} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
